import { ReactElement, FC, useContext, useEffect, useRef, useState } from 'react'
import StarRatings from 'react-star-ratings'
import { getDatabase, ref, onValue, runTransaction } from 'firebase/database'
import styled from 'styled-components'
import { AiFillStar } from 'react-icons/ai'
import { YearContext } from 'contexts'
import { VOTING_YEAR, voteStorageKey } from '../../utils/voteStorageKey'

interface UserVoteProps {
  starRatedColor: string
  starEmptyColor: string
  starHoverColor: string
  starDimension: string
  tippId: string
  category: string
  disabled?: boolean
  handleUpdateRatingCount: (rating: number) => void
  /** Fired only once the vote is committed to the database. */
  onVoted?: (category: string) => void
}

const UserVote: FC<UserVoteProps> = ({
  starRatedColor,
  starEmptyColor,
  starHoverColor,
  starDimension,
  tippId,
  category,
  disabled = false,
  handleUpdateRatingCount,
  onVoted,
}): ReactElement => {
  const year = useContext(YearContext)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [userRating, setUserRating] = useState<number | undefined>(-1)
  const [userChoice, setUserChoice] = useState<number | undefined>(-1)
  const [userIsRating, setUserIsRating] = useState(false)
  const [audienceRating, setAudienceRating] = useState(-1)
  const [error, setError] = useState<string | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const localStorageKey = voteStorageKey(year, category)

  useEffect(() => () => clearTimeout(timeoutRef.current), [])

  const handleAddVote = () => {
    const db = getDatabase()

    setIsSubmitting(true)
    setError(null)

    const choice = userChoice && userChoice > 0 ? userChoice : 0
    const postRef = ref(db, 'votes/' + tippId + '/' + choice + '/votes')

    // The database queues writes and retries indefinitely when it is
    // unreachable, so the transaction never rejects on its own.
    const fail = () => {
      clearTimeout(timeoutRef.current)
      setIsSubmitting(false)
      setError('Le vote n’a pas pu être enregistré, réessayez.')
    }
    timeoutRef.current = setTimeout(fail, 10000)

    runTransaction(postRef, (post) => {
      if (post) {
        post += 1
      } else {
        post = 1
      }
      return post
    })
      .then(({ committed }) => {
        if (!committed) {
          throw new Error('Transaction aborted')
        }
        // Only lock the category once the vote actually reached the database,
        // otherwise a failed write would block this browser forever.
        clearTimeout(timeoutRef.current)
        localStorage.setItem(localStorageKey, choice.toString())
        setUserRating(choice)
        setIsSubmitting(false)
        setUserIsRating(false)
        setError(null)
        onVoted?.(category)
      })
      .catch(fail)
  }

  const handleRate = (newRating: number) => {
    setUserIsRating(true)
    setUserChoice(newRating)
  }

  const handleCancelRate = () => {
    setUserIsRating(false)
    setError(null)
  }

  const popContent = !isSubmitting ? (
    <ConfirmContainer>
      <div>Votre note</div>
      <div>
        {new Array(userChoice && userChoice > 0 ? userChoice : 0)
          .fill(0)
          .map((s, i) => (
            <AiFillStar key={`start-${i}`} />
          ))}
      </div>
      <div style={{ marginTop: 10 }}>
        <div className='sure' style={{ marginBottom: "1em" }}>Confirmer?</div>
        <ButtonsContainer>
          <Button onClick={handleAddVote}>
            <div>Oui</div>
          </Button>
          <Button onClick={handleCancelRate}>
            <div>Non</div>
          </Button>
        </ButtonsContainer>
      </div>
      <div style={{ marginTop: 10, lineHeight: 1 }}>
        {error ? (
          <small style={{ color: '#ff515f', fontSize: 12 }}>{error}</small>
        ) : (
          <small style={{ color: 'white', fontSize: 12, opacity: 0.5 }}>
            Vous ne pouvez voter que pour un seul projet par catégorie.
          </small>
        )}
      </div>
    </ConfirmContainer>
  ) : (
    <ConfirmContainer>
      <div>Envoi de votre vote…</div>
    </ConfirmContainer>
  )

  useEffect(() => {
    const db = getDatabase()
    const votes = ref(db, 'votes/' + tippId)

    const unsubscribe = onValue(votes, (snapshot) => {
      const oneStarVotes = snapshot.child('1/votes').val()
      const twoStarVotes = snapshot.child('2/votes').val()
      const threeStarVotes = snapshot.child('3/votes').val()
      const fourStarVotes = snapshot.child('4/votes').val()
      const fiveStarVotes = snapshot.child('5/votes').val()

      // console.log('votes', tippId, snapshot.val())

      const totalStars =
        oneStarVotes +
        twoStarVotes * 2 +
        threeStarVotes * 3 +
        fourStarVotes * 4 +
        fiveStarVotes * 5
      const totalVotes =
        oneStarVotes +
        twoStarVotes +
        threeStarVotes +
        fourStarVotes +
        fiveStarVotes
      const averageVote = totalStars / totalVotes

      setAudienceRating(averageVote)

      handleUpdateRatingCount(totalVotes)
      // console.log('Set new value', tippId)
      // Recomputed on every snapshot so switching edition resets the lock
      const storedRating = localStorage.getItem(localStorageKey)
      setUserRating(storedRating ? parseInt(storedRating) : -1)
    })

    return unsubscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tippId, localStorageKey])

  // useEffect(() => {
  //   console.log('userRating', tippId, userRating)
  // }, [userRating])

  return (
    <Container>
      {/* {userRating} */}
      <StarRatings
        rating={
          userIsRating
            ? userChoice
            : userRating === undefined
            ? audienceRating
              ? parseFloat((Math.round(audienceRating * 2) / 2).toFixed(1)) // round to nearest .5
              : 0
            : audienceRating
            ? parseFloat((Math.round(audienceRating * 2) / 2).toFixed(1)) // round to nearest .5
            : 0
        }
        starRatedColor={starRatedColor}
        starEmptyColor={starEmptyColor}
        svgIconPath='M24 1.175l7.553 14.84L48 18.611 36.22 30.38l2.613 16.444L24 39.26 9.167 46.825 11.78 30.38 0 18.613l16.447-2.598z'
        starDimension={starDimension}
        starSpacing={'2px'}
        starHoverColor={starHoverColor}
        changeRating={
          year === VOTING_YEAR && userRating === -1 && !disabled
            ? handleRate
            : undefined
        }
        numberOfStars={5}
        name='rating'
      />
      {userIsRating && popContent}
    </Container>
  )
}

const Container = styled.div`
  position: relative;

  // &:hover {
  //   &:after {
  //     content: 'Voter';
  //     position: absolute;
  //     bottom: -20px;
  //     left: 0;
  //   }
  // }
`

const ConfirmContainer = styled.div`
  position: absolute;
  bottom: 40px;
  left: -20px;
  font-family: var(--font-sans-serif);
  background: #151515;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
  color: white;
  padding: 18px;
  text-align: center;
  border-radius: 10px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.2);
`

const ButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 10px;
`

const Button = styled.div`
  display: inline-block;
  color: black;
  background: white;
  padding: 5px 20px 6px 20px;
  cursor: pointer;
  text-align: center;
  border-radius: 50px;
`

export default UserVote
